import { useState, useEffect, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
// mui
import {
  useTheme,
  Box,
  Typography,
  Avatar,
  CircularProgress,
  Chip,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  ListItemSecondaryAction,
  Card,
  CardContent,
  alpha,
  FormControl,
  InputLabel,
  Select,
  TextField,
  InputAdornment,
  SelectChangeEvent,
  IconButton,
  Tooltip,
  MenuItem,
} from "@mui/material";
import {
  Business,
  Phone,
  VisibilityOutlined,
  Close,
  People,
  AttachMoney,
  AccessTime,
  LocationOn,
  Email,
  Notifications,
  Event,
  Shield,
  Search,
  FilterList,
  Clear,
  Refresh,
  DeleteOutline,
} from "@mui/icons-material";
// components
import { Main } from "layouts/main";
// redux
import { uiActions } from "store/ui/ui-slice";
// utils
import { getRequest, postRequest } from "utils/request";
import member from "../../assets/imgs/oui_users (2).svg";
import { MediaFolders } from "enums/request";

// Simple Entity interface with owner email
interface SimpleEntity {
  id: string;
  name: string;
  tier: string | null;
  account_type: string | null;
  avatar: string | null;
  phone: string;
  owner_email?: string; // Added owner email
}

// API response type
interface SimpleApiResponse {
  status: string;
  count: number;
  entities: SimpleEntity[];
  filters_applied?: {
    tier: string;
    account_type: string;
  };
}

// Type for getRequest response
interface GetRequestResponse {
  data?: SimpleApiResponse;
}

// Entity Details Interface
interface EntityDetails {
  entity: {
    id: string;
    name: string;
    moto: string | null;
    timer_approaching_mins: number | null;
    timer_exceeded_mins: number | null;
    daily_limit: string | null;
    max_offline_tasks: number | null;
    pay_rate: string;
    pay_currency: string;
    status: number;
    status_display?: string;
    prev_state: number | null;
    avatar: string | null;
    phone: string;
    desktop_notification: boolean;
    mobile_notification: boolean;
    notify_after: number | null;
    address: string;
    daily_summary_time: string | null;
    timezone_offset: string;
    creation_date: string;
  };
  members: Array<{
    member_id: string;
    firstname: string;
    lastname: string;
    full_name: string;
    email: string;
    avatar: string | null;
    phone: string;
    member_status: number;
    member_status_display?: string;
    is_temp: boolean;
    primary_entity_id: string | null;
    membership_id: string;
    membership_status: number;
    membership_status_display?: string;
    role: number;
    role_display?: string;
    prev_state: number | null;
    member_pay_rate: string;
    member_pay_currency: string;
    membership_creation_date: string;
  }>;
  total_members?: number;
  active_members?: number;
  owners?: number;
}

// Filter Options Interface
interface FilterOptions {
  tier: string;
  accountType: string;
  searchTerm: string;
}

const Entities = () => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // Get app state from Redux for user info
  const app = useSelector((state: any) => state.app);

  const [entities, setEntities] = useState<SimpleEntity[]>([]);
  const [filteredEntities, setFilteredEntities] = useState<SimpleEntity[]>([]);
  const [loadingEntities, setLoadingEntities] = useState(false);
  const [entitiesError, setEntitiesError] = useState<string | null>(null);
  const [entityImages, setEntityImages] = useState<Record<string, string>>({});
  const [totalCount, setTotalCount] = useState<number>(0);

  // Filter states
  const [filters, setFilters] = useState<FilterOptions>({
    tier: "",
    accountType: "",
    searchTerm: "",
  });

  // Debounce state for search
  const [searchTimeout, setSearchTimeout] = useState<NodeJS.Timeout | null>(
    null,
  );

  // State for entity details popup
  const [selectedEntityDetails, setSelectedEntityDetails] =
    useState<EntityDetails | null>(null);
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [detailsError, setDetailsError] = useState<string | null>(null);
  const [deletingEntity, setDeletingEntity] = useState<string | null>(null);

  // Function to fetch entity image
  const fetchEntityImage = async (
    entityId: string,
    avatarFilename: string,
  ): Promise<string | null> => {
    if (!avatarFilename) return null;

    try {
      const response = await getRequest(
        `get_file/`,
        {
          folder: MediaFolders.ENTITY_IMAGES,
          filename: avatarFilename,
          as_attachment: false,
        },
        navigate,
        {},
        "blob",
      );

      if (response?.data) {
        const imageUrl = URL.createObjectURL(new Blob([response.data]));
        return imageUrl;
      }
      return null;
    } catch (error) {
      console.error("Error fetching entity image:", error);
      return null;
    }
  };

  // Function to get owner's email from members array
  const getOwnerEmailFromMembers = (members: any[]): string | null => {
    const owner = members.find((member) => member.role === 1); // role 1 is typically owner
    return owner?.email || null;
  };

  // Function to delete entity
  const handleDeleteEntity = async (entityId: string, entityName: string) => {
    // Confirm deletion
    const confirmed = window.confirm(
      `Are you sure you want to delete "${entityName}"? This action cannot be undone.`,
    );

    if (!confirmed) return;

    setDeletingEntity(entityId);

    try {
      const response = await postRequest(
        `delete_entity/`,
        {
          entity_id: entityId,
        },
        navigate,
      );

      console.log(response?.data);

      if (!response?.data?.isOk) {
        dispatch(
          uiActions.updateNotification({
            message: response?.data?.message || "Failed to delete entity",
            level: "error",
          }),
        );
      } else {
        dispatch(
          uiActions.updateNotification({
            message: `Entity "${entityName}" deleted successfully`,
            level: "success",
          }),
        );

        // Refresh the entities list
        await fetchEntities();

        // Close the details dialog if it's open for this entity
        if (
          detailsDialogOpen &&
          selectedEntityDetails?.entity.id === entityId
        ) {
          handleCloseDetailsDialog();
        }
      }
    } catch (error: any) {
      console.error("Error deleting entity:", error);
      dispatch(
        uiActions.updateNotification({
          message:
            error?.message || "An error occurred while deleting the entity",
          level: "error",
        }),
      );
    } finally {
      setDeletingEntity(null);
    }
  };

  // Function to fetch entity details for popup
  const fetchEntityDetails = async (entityId: string) => {
    setLoadingDetails(true);
    setDetailsError(null);

    try {
      // Call the API endpoint to get detailed entity information
      const response = (await getRequest(
        `entity/${entityId}/`,
        {},
        navigate,
      )) as any;

      // Check the response structure based on the error you provided
      if (response?.data?.isOk && response.data.data) {
        setSelectedEntityDetails(response.data.data);
        setDetailsDialogOpen(true);
      } else if (response?.data) {
        // If there's no isOk flag but data exists directly
        setSelectedEntityDetails(response.data);
        setDetailsDialogOpen(true);
      } else {
        setDetailsError("Failed to fetch entity details");
        dispatch(
          uiActions.updateNotification({
            message: "Failed to fetch entity details",
            level: "error",
          }),
        );
      }
    } catch (error: any) {
      setDetailsError(error.message || "An error occurred");
      dispatch(
        uiActions.updateNotification({
          message: "Failed to load entity details",
          level: "error",
        }),
      );
    } finally {
      setLoadingDetails(false);
    }
  };

  // Function to fetch detailed entity data including owner email
  const fetchEntityData = async (
    entityId: string,
  ): Promise<SimpleEntity | null> => {
    try {
      // Fetch entity details to get members and find owner
      const response = (await getRequest(
        `entity/${entityId}/`,
        {},
        navigate,
      )) as any;

      if (response?.data?.isOk && response.data.data) {
        const data = response.data.data;
        // Find owner email from members
        const ownerEmail = getOwnerEmailFromMembers(data.members);

        // Return entity with owner email
        return {
          id: data.entity.id,
          name: data.entity.name,
          tier: null, // You might need to get this from another endpoint
          account_type: null, // You might need to get this from another endpoint
          avatar: data.entity.avatar,
          phone: data.entity.phone,
          owner_email: ownerEmail || "No owner",
        };
      }
      return null;
    } catch (error) {
      console.error("Error fetching entity data:", error);
      return null;
    }
  };

  // Fetch entities with current filters
  const fetchEntities = useCallback(async () => {
    setLoadingEntities(true);
    setEntitiesError(null);

    try {
      // Build query parameters based on current filters
      const params: Record<string, string> = {};
      if (filters.tier) params.tier = filters.tier;
      if (filters.accountType) params.account_type = filters.accountType;

      const response = (await getRequest(
        "entities/",
        params,
        navigate,
      )) as unknown as GetRequestResponse;

      if (response?.data) {
        const apiResponse = response.data;
        if (apiResponse.status === "success" && apiResponse.entities) {
          setTotalCount(apiResponse.count);

          // Fetch owner emails for each entity
          const entitiesWithOwnerEmails = await Promise.all(
            apiResponse.entities.map(async (entity: SimpleEntity) => {
              try {
                const detailedEntity = await fetchEntityData(entity.id);
                if (detailedEntity) {
                  return {
                    ...entity,
                    owner_email: detailedEntity.owner_email,
                  };
                }
                return entity;
              } catch (error) {
                console.error(
                  `Error fetching owner email for entity ${entity.id}:`,
                  error,
                );
                return entity;
              }
            }),
          );

          setEntities(entitiesWithOwnerEmails);

          // Apply local search filter if there's a search term
          if (filters.searchTerm) {
            const searchLower = filters.searchTerm.toLowerCase();
            const filtered = entitiesWithOwnerEmails.filter(
              (entity) =>
                entity.name.toLowerCase().includes(searchLower) ||
                (entity.phone &&
                  entity.phone.toLowerCase().includes(searchLower)) ||
                (entity.owner_email &&
                  entity.owner_email.toLowerCase().includes(searchLower)),
            );
            setFilteredEntities(filtered);
          } else {
            setFilteredEntities(entitiesWithOwnerEmails);
          }

          // Fetch images for entities
          const imagePromises = entitiesWithOwnerEmails.map(
            async (entity: SimpleEntity) => {
              if (entity.avatar) {
                const imageUrl = await fetchEntityImage(
                  entity.id,
                  entity.avatar,
                );
                if (imageUrl) {
                  return { id: entity.id, url: imageUrl };
                }
              }
              return null;
            },
          );

          const images = await Promise.all(imagePromises);
          const newImageMap: Record<string, string> = {};
          images.forEach((img) => {
            if (img) {
              newImageMap[img.id] = img.url;
            }
          });
          setEntityImages(newImageMap);
        } else {
          setEntitiesError("Failed to fetch entities");
          dispatch(
            uiActions.updateNotification({
              message: "Failed to fetch entities",
              level: "error",
            }),
          );
        }
      }
    } catch (error: any) {
      setEntitiesError(error.message || "An error occurred");
      dispatch(
        uiActions.updateNotification({
          message: "Failed to load entities",
          level: "error",
        }),
      );
    } finally {
      setLoadingEntities(false);
    }
  }, [
    filters.tier,
    filters.accountType,
    filters.searchTerm,
    dispatch,
    navigate,
  ]);

  // Handle filter changes - automatically apply
  const handleFilterChange = useCallback(
    (field: keyof FilterOptions, value: string) => {
      const newFilters = { ...filters, [field]: value };
      setFilters(newFilters);
    },
    [filters],
  );

  // Handle search with debounce
  const handleSearchChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const searchTerm = event.target.value;

      // Clear existing timeout
      if (searchTimeout) {
        clearTimeout(searchTimeout);
      }

      // Set new timeout for debouncing
      const timeout = setTimeout(() => {
        handleFilterChange("searchTerm", searchTerm);
      }, 300); // 300ms debounce

      setSearchTimeout(timeout);
    },
    [handleFilterChange, searchTimeout],
  );

  // Clear specific filter and refresh
  const handleClearFilter = useCallback((filterName: keyof FilterOptions) => {
    setFilters((prev) => ({ ...prev, [filterName]: "" }));
  }, []);

  // Clear all filters
  const handleClearAllFilters = useCallback(() => {
    setFilters({
      tier: "",
      accountType: "",
      searchTerm: "",
    });
  }, []);

  // Refresh data
  const handleRefresh = useCallback(() => {
    fetchEntities();
  }, [fetchEntities]);

  // Load data on mount and when filters change
  useEffect(() => {
    fetchEntities();
  }, [fetchEntities]);

  // Apply local search filter when search term changes
  useEffect(() => {
    if (filters.searchTerm) {
      const searchLower = filters.searchTerm.toLowerCase();
      const filtered = entities.filter(
        (entity) =>
          entity.name.toLowerCase().includes(searchLower) ||
          (entity.phone && entity.phone.toLowerCase().includes(searchLower)) ||
          (entity.owner_email &&
            entity.owner_email.toLowerCase().includes(searchLower)),
      );
      setFilteredEntities(filtered);
    } else {
      setFilteredEntities(entities);
    }
  }, [filters.searchTerm, entities]);

  // Cleanup
  useEffect(() => {
    return () => {
      Object.values(entityImages).forEach((url) => {
        if (url.startsWith("blob:")) {
          URL.revokeObjectURL(url);
        }
      });
      if (searchTimeout) {
        clearTimeout(searchTimeout);
      }
    };
  }, [entityImages, searchTimeout]);

  // Helper function to safely get the image URL
  const getEntityImageUrl = (entityId: string, entityAvatar: string | null) => {
    if (entityImages[entityId]) {
      return entityImages[entityId];
    }

    if (entityAvatar && !entityImages[entityId]) {
      setTimeout(() => {
        fetchEntityImage(entityId, entityAvatar).then((url) => {
          if (url) {
            setEntityImages((prev) => ({
              ...prev,
              [entityId]: url,
            }));
          }
        });
      }, 0);
    }

    return undefined;
  };

  const handleViewEntityDetails = (entityId: string) => {
    fetchEntityDetails(entityId);
  };

  const handleCloseDetailsDialog = () => {
    setDetailsDialogOpen(false);
    setSelectedEntityDetails(null);
    setDetailsError(null);
  };

  const getSubscriptionTierColor = (tier: string | null) => {
    if (!tier) return "default";

    const tierLower = tier.toLowerCase();
    switch (tierLower) {
      case "starter":
        return "primary";
      case "professional":
        return "success";
      case "enterprise":
        return "warning";
      default:
        return "default";
    }
  };

  const getAccountTypeColor = (accountType: string | null) => {
    if (!accountType) return "default";

    const typeLower = accountType.toLowerCase();
    switch (typeLower) {
      case "individual":
        return "info";
      case "team":
        return "secondary";
      default:
        return "default";
    }
  };

  const getStatusColor = (status: number) => {
    switch (status) {
      case 1: // ACTIVE
        return "success";
      case 2: // INACTIVE
        return "error";
      case 3: // SUSPENDED
        return "warning";
      default:
        return "default";
    }
  };

  const formatCurrency = (amount: string, currency: string) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: currency || "USD",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(parseFloat(amount) || 0);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const renderEntityRow = (entity: SimpleEntity) => {
    const tierColor = getSubscriptionTierColor(entity.tier);
    const accountTypeColor = getAccountTypeColor(entity.account_type);
    const imageUrl = getEntityImageUrl(entity.id, entity.avatar);
    const isDeleting = deletingEntity === entity.id;

    return (
      <TableRow
        key={entity.id}
        sx={{
          "&:hover": {
            backgroundColor: "action.hover",
          },
          transition: "background-color 0.2s",
        }}
      >
        {/* Avatar and Name */}
        <TableCell>
          <Box display="flex" alignItems="center">
            <Avatar
              src={imageUrl}
              alt={entity.name}
              sx={{
                width: 40,
                height: 40,
                mr: 2,
                bgcolor: imageUrl ? "transparent" : theme.palette.primary.light,
              }}
            >
              {!imageUrl && entity.name?.charAt(0).toUpperCase()}
            </Avatar>
            <Box>
              <Typography variant="body1" fontWeight="500">
                {entity.name}
              </Typography>
              <Box display="flex" alignItems="center" mt={0.5} gap={1}>
                {/* Phone */}
                <Box display="flex" alignItems="center">
                  <Phone
                    sx={{ fontSize: 14, mr: 0.5, color: "text.secondary" }}
                  />
                  <Typography variant="body2" color="text.secondary">
                    {entity.phone || "No phone"}
                  </Typography>
                </Box>

                {/* Divider */}
                <Typography variant="body2" color="text.secondary">
                  •
                </Typography>

                {/* Owner Email */}
                <Box display="flex" alignItems="center">
                  <Email
                    sx={{ fontSize: 14, mr: 0.5, color: "text.secondary" }}
                  />
                  <Typography variant="body2" color="text.secondary">
                    {entity.owner_email || "No owner"}
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Box>
        </TableCell>

        {/* Subscription Tier */}
        <TableCell>
          {entity.tier ? (
            <Chip
              label={entity.tier}
              size="small"
              color={tierColor as any}
              variant="outlined"
              sx={{ fontWeight: 500 }}
            />
          ) : (
            <Typography variant="body2" color="text.disabled">
              No tier
            </Typography>
          )}
        </TableCell>

        {/* Account Type */}
        <TableCell>
          {entity.account_type ? (
            <Chip
              label={entity.account_type}
              size="small"
              color={accountTypeColor as any}
              variant="outlined"
              sx={{ fontWeight: 500 }}
            />
          ) : (
            <Typography variant="body2" color="text.disabled">
              Not specified
            </Typography>
          )}
        </TableCell>

        {/* Actions */}
        <TableCell align="right">
          <Box
            display="flex"
            justifyContent="flex-end"
            alignItems="center"
            gap={1}
          >
            <Button
              variant="outlined"
              size="small"
              startIcon={<VisibilityOutlined />}
              onClick={() => handleViewEntityDetails(entity.id)}
              sx={{
                borderRadius: 1.5,
                textTransform: "none",
                fontWeight: 500,
                minWidth: 80,
              }}
            >
              View
            </Button>
            <Button
              variant="outlined"
              size="small"
              color="error"
              startIcon={
                isDeleting ? <CircularProgress size={16} /> : <DeleteOutline />
              }
              onClick={() => handleDeleteEntity(entity.id, entity.name)}
              disabled={isDeleting}
              sx={{
                borderRadius: 1.5,
                textTransform: "none",
                fontWeight: 500,
                minWidth: 80,
              }}
            >
              {isDeleting ? "Deleting..." : "Delete"}
            </Button>
          </Box>
        </TableCell>
      </TableRow>
    );
  };

  // Entity Details Dialog Component
  const EntityDetailsDialog = () => {
    if (!selectedEntityDetails) return null;

    const isDeleting = deletingEntity === selectedEntityDetails.entity.id;

    return (
      <Dialog
        open={detailsDialogOpen}
        onClose={handleCloseDetailsDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <Typography variant="h6" fontWeight="bold">
              Entity Details
            </Typography>
            <IconButton
              onClick={handleCloseDetailsDialog}
              style={{ width: "20px" }}
              size="small"
            >
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent dividers>
          {loadingDetails ? (
            <Box display="flex" justifyContent="center" py={4}>
              <CircularProgress />
            </Box>
          ) : detailsError ? (
            <Box textAlign="center" py={2}>
              <Typography color="error">{detailsError}</Typography>
            </Box>
          ) : selectedEntityDetails ? (
            <Box>
              {/* Header with Avatar and Basic Info */}
              <Box
                display="flex"
                alignItems="center"
                mb={3}
                pb={2}
                borderBottom={1}
                borderColor="divider"
              >
                <Avatar
                  src={getEntityImageUrl(
                    selectedEntityDetails.entity.id,
                    selectedEntityDetails.entity.avatar,
                  )}
                  alt={selectedEntityDetails.entity.name}
                  sx={{
                    width: 80,
                    height: 80,
                    mr: 3,
                    bgcolor: theme.palette.primary.light,
                    fontSize: "2rem",
                  }}
                >
                  {selectedEntityDetails.entity.name?.charAt(0).toUpperCase()}
                </Avatar>
                <Box>
                  <Typography variant="h4" fontWeight="bold" gutterBottom>
                    {selectedEntityDetails.entity.name}
                  </Typography>
                  {selectedEntityDetails.entity.moto && (
                    <Typography
                      variant="subtitle1"
                      color="text.secondary"
                      fontStyle="italic"
                      gutterBottom
                    >
                      "{selectedEntityDetails.entity.moto}"
                    </Typography>
                  )}
                  <Box display="flex" gap={1} flexWrap="wrap">
                    <Chip
                      label={
                        selectedEntityDetails.entity.status_display ||
                        `Status: ${selectedEntityDetails.entity.status}`
                      }
                      color={
                        getStatusColor(
                          selectedEntityDetails.entity.status,
                        ) as any
                      }
                      size="small"
                    />
                    <Chip
                      icon={<People fontSize="small" />}
                      label={`${selectedEntityDetails.members.length} Members`}
                      variant="outlined"
                      size="small"
                    />
                    {selectedEntityDetails.owners && (
                      <Chip
                        icon={<Shield fontSize="small" />}
                        label={`${selectedEntityDetails.owners} Owners`}
                        variant="outlined"
                        size="small"
                      />
                    )}
                  </Box>
                </Box>
              </Box>

              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                  gap: 3,
                }}
              >
                {/* Entity Information Card */}
                <Box>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography
                        variant="h6"
                        gutterBottom
                        fontWeight="bold"
                        display="flex"
                        alignItems="center"
                        gap={1}
                      >
                        <Business fontSize="small" /> Entity Information
                      </Typography>

                      <List dense disablePadding>
                        <ListItem disableGutters>
                          <ListItemText
                            primary="Phone"
                            secondary={
                              selectedEntityDetails.entity.phone ||
                              "Not provided"
                            }
                            secondaryTypographyProps={{ color: "text.primary" }}
                          />
                          <Phone color="action" />
                        </ListItem>

                        {/* Owner Email Section */}
                        <ListItem disableGutters>
                          <ListItemText
                            primary="Owner Email"
                            secondary={
                              selectedEntityDetails.members.find(
                                (m) => m.role === 1,
                              )?.email || "Not available"
                            }
                            secondaryTypographyProps={{ color: "text.primary" }}
                          />
                          <Email color="action" />
                        </ListItem>

                        <ListItem disableGutters>
                          <ListItemText
                            primary="Address"
                            secondary={
                              selectedEntityDetails.entity.address ||
                              "Not provided"
                            }
                            secondaryTypographyProps={{ color: "text.primary" }}
                          />
                          <LocationOn color="action" />
                        </ListItem>

                        <ListItem disableGutters>
                          <ListItemText
                            primary="Created"
                            secondary={formatDate(
                              selectedEntityDetails.entity.creation_date,
                            )}
                            secondaryTypographyProps={{ color: "text.primary" }}
                          />
                          <Event color="action" />
                        </ListItem>

                        <ListItem disableGutters>
                          <ListItemText
                            primary="Pay Rate"
                            secondary={formatCurrency(
                              selectedEntityDetails.entity.pay_rate,
                              selectedEntityDetails.entity.pay_currency,
                            )}
                            secondaryTypographyProps={{ color: "text.primary" }}
                          />
                          <AttachMoney color="action" />
                        </ListItem>

                        {selectedEntityDetails.entity.daily_limit && (
                          <ListItem disableGutters>
                            <ListItemText
                              primary="Daily Limit"
                              secondary={formatCurrency(
                                selectedEntityDetails.entity.daily_limit,
                                selectedEntityDetails.entity.pay_currency,
                              )}
                              secondaryTypographyProps={{
                                color: "text.primary",
                              }}
                            />
                            <AccessTime color="action" />
                          </ListItem>
                        )}
                      </List>
                    </CardContent>
                  </Card>
                </Box>

                {/* Settings and Timers Card */}
                <Box>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography
                        variant="h6"
                        gutterBottom
                        fontWeight="bold"
                        display="flex"
                        alignItems="center"
                        gap={1}
                      >
                        <Notifications fontSize="small" /> Settings
                      </Typography>

                      <List dense disablePadding>
                        <ListItem disableGutters>
                          <ListItemText
                            primary="Desktop Notifications"
                            secondary={
                              selectedEntityDetails.entity.desktop_notification
                                ? "Enabled"
                                : "Disabled"
                            }
                            secondaryTypographyProps={{
                              color: selectedEntityDetails.entity
                                .desktop_notification
                                ? "success.main"
                                : "text.disabled",
                            }}
                          />
                        </ListItem>

                        <ListItem disableGutters>
                          <ListItemText
                            primary="Mobile Notifications"
                            secondary={
                              selectedEntityDetails.entity.mobile_notification
                                ? "Enabled"
                                : "Disabled"
                            }
                            secondaryTypographyProps={{
                              color: selectedEntityDetails.entity
                                .mobile_notification
                                ? "success.main"
                                : "text.disabled",
                            }}
                          />
                        </ListItem>

                        <ListItem disableGutters>
                          <ListItemText
                            primary="Notify After"
                            secondary={`${
                              selectedEntityDetails.entity.notify_after || 60
                            } minutes`}
                            secondaryTypographyProps={{ color: "text.primary" }}
                          />
                        </ListItem>

                        {selectedEntityDetails.entity
                          .timer_approaching_mins && (
                          <ListItem disableGutters>
                            <ListItemText
                              primary="Timer Approaching"
                              secondary={`${selectedEntityDetails.entity.timer_approaching_mins} mins`}
                              secondaryTypographyProps={{
                                color: "text.primary",
                              }}
                            />
                            <AccessTime color="action" />
                          </ListItem>
                        )}

                        {selectedEntityDetails.entity.timer_exceeded_mins && (
                          <ListItem disableGutters>
                            <ListItemText
                              primary="Timer Exceeded"
                              secondary={`${selectedEntityDetails.entity.timer_exceeded_mins} mins`}
                              secondaryTypographyProps={{
                                color: "text.primary",
                              }}
                            />
                            <AccessTime color="action" />
                          </ListItem>
                        )}

                        {selectedEntityDetails.entity.daily_summary_time && (
                          <ListItem disableGutters>
                            <ListItemText
                              primary="Daily Summary"
                              secondary={`${selectedEntityDetails.entity.daily_summary_time}`}
                              secondaryTypographyProps={{
                                color: "text.primary",
                              }}
                            />
                            <Event color="action" />
                          </ListItem>
                        )}

                        {selectedEntityDetails.entity.max_offline_tasks && (
                          <ListItem disableGutters>
                            <ListItemText
                              primary="Max Offline Tasks"
                              secondary={
                                selectedEntityDetails.entity.max_offline_tasks
                              }
                              secondaryTypographyProps={{
                                color: "text.primary",
                              }}
                            />
                          </ListItem>
                        )}
                      </List>
                    </CardContent>
                  </Card>
                </Box>

                {/* Members List Card */}
                <Box sx={{ gridColumn: { xs: "1 / -1", md: "1 / -1" } }}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography
                        variant="h6"
                        gutterBottom
                        fontWeight="bold"
                        display="flex"
                        alignItems="center"
                        gap={1}
                      >
                        <People fontSize="small" /> Members (
                        {selectedEntityDetails.members.length})
                      </Typography>

                      <List sx={{ maxHeight: 300, overflow: "auto" }}>
                        {selectedEntityDetails.members.map((member) => (
                          <ListItem
                            key={member.membership_id}
                            divider
                            sx={{
                              borderRadius: 1,
                              mb: 1,
                              backgroundColor:
                                member.role === 1
                                  ? alpha(theme.palette.primary.main, 0.05)
                                  : "transparent",
                              borderLeft:
                                member.role === 1
                                  ? `4px solid ${theme.palette.primary.main}`
                                  : "none",
                            }}
                          >
                            <ListItemAvatar>
                              <Avatar
                                src={member.avatar || undefined}
                                alt={member.full_name}
                                sx={{
                                  bgcolor: member.avatar
                                    ? "transparent"
                                    : theme.palette.secondary.light,
                                }}
                              >
                                {member.firstname?.charAt(0).toUpperCase()}
                              </Avatar>
                            </ListItemAvatar>

                            <ListItemText
                              primary={
                                <Box display="flex" alignItems="center" gap={1}>
                                  <Typography
                                    variant="subtitle1"
                                    fontWeight="medium"
                                  >
                                    {member.full_name}
                                  </Typography>
                                  {member.role === 1 && (
                                    <Chip
                                      label="Owner"
                                      size="small"
                                      color="primary"
                                      variant="outlined"
                                      sx={{ height: 20 }}
                                    />
                                  )}
                                </Box>
                              }
                              secondary={
                                <Box>
                                  <Typography
                                    variant="body2"
                                    color="text.secondary"
                                  >
                                    {member.email}
                                  </Typography>
                                  <Typography
                                    variant="body2"
                                    color="text.secondary"
                                  >
                                    {member.phone}
                                  </Typography>
                                </Box>
                              }
                            />

                            <ListItemSecondaryAction>
                              <Box
                                display="flex"
                                flexDirection="column"
                                alignItems="flex-end"
                                gap={0.5}
                              >
                                <Chip
                                  label={
                                    member.membership_status_display ||
                                    `Status: ${member.membership_status}`
                                  }
                                  size="small"
                                  color={
                                    getStatusColor(
                                      member.membership_status,
                                    ) as any
                                  }
                                  variant="outlined"
                                  sx={{ height: 22 }}
                                />
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                >
                                  {formatCurrency(
                                    member.member_pay_rate,
                                    member.member_pay_currency,
                                  )}
                                </Typography>
                              </Box>
                            </ListItemSecondaryAction>
                          </ListItem>
                        ))}
                      </List>
                    </CardContent>
                  </Card>
                </Box>
              </Box>
            </Box>
          ) : null}
        </DialogContent>

        <DialogActions>
          <Button
            color="error"
            variant="outlined"
            onClick={() =>
              handleDeleteEntity(
                selectedEntityDetails?.entity.id,
                selectedEntityDetails?.entity.name,
              )
            }
            startIcon={
              isDeleting ? <CircularProgress size={16} /> : <DeleteOutline />
            }
            disabled={isDeleting}
          >
            {isDeleting ? "Deleting..." : "Delete Entity"}
          </Button>
          <Button onClick={handleCloseDetailsDialog}>Close</Button>
        </DialogActions>
      </Dialog>
    );
  };

  // Filter Controls Component
  const FilterControls = () => (
    <Paper sx={{ p: 2, mb: 3, borderRadius: 2 }}>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={2}
      >
        <Box display="flex" alignItems="center" gap={2}>
          <FilterList color="action" />
          <Typography variant="subtitle2" fontWeight="600">
            Filter Entities
          </Typography>
        </Box>
      </Box>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", md: "1fr 1fr 1fr" },
          gap: 2,
          mb: 2,
        }}
      >
        {/* Tier Filter */}
        <Box>
          <FormControl fullWidth size="small">
            <InputLabel>Subscription Tier</InputLabel>
            <Select
              value={filters.tier}
              label="Subscription Tier"
              onChange={(e) => handleFilterChange("tier", e.target.value)}
              endAdornment={
                filters.tier && (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      onClick={() => handleClearFilter("tier")}
                      edge="end"
                    >
                      <Clear fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                )
              }
            >
              <MenuItem value="">All Tiers</MenuItem>
              <MenuItem value="Starter">Starter</MenuItem>
              <MenuItem value="Professional">Professional</MenuItem>
              <MenuItem value="Enterprise">Enterprise</MenuItem>
            </Select>
          </FormControl>
        </Box>

        {/* Account Type Filter */}
        <Box>
          <FormControl fullWidth size="small">
            <InputLabel>Account Type</InputLabel>
            <Select
              value={filters.accountType}
              label="Account Type"
              onChange={(e) =>
                handleFilterChange("accountType", e.target.value)
              }
              endAdornment={
                filters.accountType && (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      onClick={() => handleClearFilter("accountType")}
                      edge="end"
                    >
                      <Clear fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                )
              }
            >
              <MenuItem value="">All Account Types</MenuItem>
              <MenuItem value="Individual">Individual</MenuItem>
              <MenuItem value="Team">Team</MenuItem>
            </Select>
          </FormControl>
        </Box>

        {/* Search Filter */}
        <Box>
          <TextField
            fullWidth
            size="small"
            label="Search entities"
            value={filters.searchTerm}
            onChange={handleSearchChange}
            placeholder="Search by name, phone, or owner email..."
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search fontSize="small" />
                </InputAdornment>
              ),
              endAdornment: filters.searchTerm && (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    onClick={() => handleClearFilter("searchTerm")}
                    edge="end"
                  >
                    <Clear fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </Box>
      </Box>

      {/* Active Filter Tags */}
      {(filters.tier || filters.accountType || filters.searchTerm) && (
        <Box>
          <Box display="flex" alignItems="center" gap={1} mb={1}>
            <Typography variant="caption" color="text.secondary">
              Active filters:
            </Typography>
          </Box>
          <Box display="flex" gap={1} flexWrap="wrap">
            {filters.tier && (
              <Chip
                label={`Tier: ${filters.tier}`}
                onDelete={() => handleClearFilter("tier")}
                color={getSubscriptionTierColor(filters.tier) as any}
                size="small"
              />
            )}
            {filters.accountType && (
              <Chip
                label={`Type: ${filters.accountType}`}
                onDelete={() => handleClearFilter("accountType")}
                color={getAccountTypeColor(filters.accountType) as any}
                size="small"
              />
            )}
            {filters.searchTerm && (
              <Chip
                label={`Search: "${filters.searchTerm}"`}
                onDelete={() => handleClearFilter("searchTerm")}
                color="default"
                size="small"
              />
            )}
          </Box>
        </Box>
      )}
    </Paper>
  );

  if (loadingEntities) {
    return (
      <Main header={{ icon: member, title: "Entities Management" }}>
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          height="60vh"
        >
          <CircularProgress size={60} />
          <Typography sx={{ mt: 3 }} variant="h6">
            Loading entities...
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Please wait while we fetch all entity data including owner emails
          </Typography>
        </Box>
      </Main>
    );
  }

  if (entitiesError) {
    return (
      <Main header={{ icon: undefined, title: "Entities Management" }}>
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          height="60vh"
        >
          <Typography color="error" variant="h6">
            Error loading entities
          </Typography>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ mt: 1, mb: 3 }}
          >
            {entitiesError}
          </Typography>
          <Button
            variant="contained"
            onClick={handleRefresh}
            startIcon={<Refresh />}
            sx={{ borderRadius: 2 }}
          >
            Try Again
          </Button>
        </Box>
      </Main>
    );
  }

  return (
    <>
      <Main header={{ icon: member, title: "Entities Management" }}>
        <Box sx={{ px: { xs: 2, md: 3 }, py: 3 }}>
          {/* Header with Count */}
          <Box sx={{ mb: 3 }}>
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              mb={2}
            >
              <Typography variant="h5" fontWeight="600">
                All Entities
              </Typography>
              <Box display="flex" alignItems="center" gap={2}>
                <Chip
                  label={`Total: ${totalCount}`}
                  color="primary"
                  variant="outlined"
                  sx={{ fontWeight: 500 }}
                />
                <Typography variant="body2" color="text.secondary">
                  Showing: {filteredEntities.length}
                </Typography>
              </Box>
            </Box>

            {/* Filter Controls */}
            <FilterControls />
          </Box>

          {/* Entities Table */}
          {filteredEntities.length === 0 ? (
            <Box
              display="flex"
              flexDirection="column"
              alignItems="center"
              justifyContent="center"
              height="40vh"
              sx={{ backgroundColor: "grey.50", borderRadius: 3 }}
            >
              <Business sx={{ fontSize: 80, color: "grey.400", mb: 2 }} />
              <Typography variant="h6" color="text.secondary">
                {entities.length === 0
                  ? "No entities found"
                  : "No matching entities"}
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mt: 1, mb: 3 }}
              >
                {entities.length === 0
                  ? "There are no entities in the system yet."
                  : "Try adjusting your filters to see more results."}
              </Typography>
              {(filters.tier || filters.accountType || filters.searchTerm) && (
                <Button
                  variant="outlined"
                  onClick={handleClearAllFilters}
                  sx={{ borderRadius: 2 }}
                >
                  Clear All Filters
                </Button>
              )}
            </Box>
          ) : (
            <Paper sx={{ width: "100%", overflow: "hidden", borderRadius: 2 }}>
              <TableContainer>
                <Table>
                  <TableHead sx={{ backgroundColor: "primary.50" }}>
                    <TableRow>
                      <TableCell sx={{ py: 2, width: "40%" }}>
                        <Typography variant="subtitle2" fontWeight="600">
                          Entity
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ py: 2 }}>
                        <Typography variant="subtitle2" fontWeight="600">
                          Subscription Tier
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ py: 2 }}>
                        <Typography variant="subtitle2" fontWeight="600">
                          Account Type
                        </Typography>
                      </TableCell>
                      <TableCell align="right" sx={{ py: 2 }}>
                        <Typography variant="subtitle2" fontWeight="600">
                          Actions
                        </Typography>
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>{filteredEntities.map(renderEntityRow)}</TableBody>
                </Table>
              </TableContainer>
            </Paper>
          )}
        </Box>
      </Main>

      {/* Entity Details Dialog */}
      <EntityDetailsDialog />
    </>
  );
};

export default Entities;
